/**
 * `/auth/invite?token=…&tenantSlug=…` — the Link Accounting invitation link.
 * Outside the signed-in tree. Setting a password signs the invitee in with
 * the `accounting` audience; the app then opens on their company.
 */
import type { JSX } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AuthLayout, InviteAcceptForm, useAuthBranding } from "@linkhr/auth/pages";
import { resolveTenant } from "@linkhr/auth";
import { env } from "../env";

const searchSchema = z.object({
  token: z.string().min(1),
  tenantSlug: z.string().min(1).optional(),
});

export const Route = createFileRoute("/auth/invite")({
  validateSearch: searchSchema,
  component: InviteRouteComponent,
});

function InviteRouteComponent(): JSX.Element {
  const { token, tenantSlug: fromLink } = Route.useSearch();
  const navigate = useNavigate();
  const tenantSlug = fromLink ?? resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG });
  const branding = useAuthBranding(tenantSlug);
  return (
    <AuthLayout branding={branding} product="accounting">
      <InviteAcceptForm
        token={token}
        tenantSlug={tenantSlug}
        apiBaseUrl={env.VITE_API_BASE_URL}
        onSuccess={() => void navigate({ to: "/", replace: true })}
        onRequestNewLink={() => void navigate({ to: "/login" })}
      />
    </AuthLayout>
  );
}
